/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../../page-modules/account/services/account-data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { DEFAULT_PASSWORD_REGEXP } from '../../../shared/constants/constants';
import { FormsHelper } from '../../../shared/helpers/forms-helper';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Organization } from '../../../shared/models';
import { TokenVerification } from '../../../shared/models/account/account.model';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { ObservableResult } from '../../../shared/store';
import { TokenType } from '../../models';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  templateUrl: './password-confirmation-and-recovery.component.html',
  styleUrls: ['./password-confirmation-and-recovery.component.scss'],
})
export class PasswordConfirmationAndRecoveryComponent implements OnInit, OnDestroy {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  passwordForm: FormGroup;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordFocusState = false;
  confirmPasswordFocusState = false;
  isEmailConfirmationPage: boolean;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  tokenType: TokenType;
  showMaintenanceOverlay: boolean;

  private organizationType: string;
  private organizationTypeSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private translocoService: TranslocoService,
    private captchaV3Service: ReCaptchaV3Service,
    private cdr: ChangeDetectorRef,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
  }

  ngOnInit() {
    this.organizationTypeSubscription = this.organizationData$.subscribe((organization) => {
      if (organization) {
        this.organizationType = organization.type;
      }
    });
    this.isEmailConfirmationPage =
      this.router.url.includes('verify-personal-email') ||
      this.router.url.includes('verify-alumni-email') ||
      this.router.url.includes('verify-organisation-email') ||
      this.router.url.includes('verify-organisation-email-with-folio');
    const token = this.activatedRoute.snapshot.queryParamMap.get('token');

    if (this.isEmailConfirmationPage) {
      this.getEmailFromToken(token);

      this.passwordForm = this.fb.group(
        {
          password: ['', Validators.compose([Validators.required, Validators.pattern(DEFAULT_PASSWORD_REGEXP)])],
          newPassword: ['', Validators.compose([Validators.required, Validators.pattern(DEFAULT_PASSWORD_REGEXP)])],
          email: ['', Validators.compose([Validators.required, Validators.email])],
        },
        {
          validator: FormsHelper.isEqual('password', 'newPassword'),
        },
      );
    } else {
      this.passwordForm = this.fb.group(
        {
          password: ['', Validators.compose([Validators.required, Validators.pattern(DEFAULT_PASSWORD_REGEXP)])],
          newPassword: ['', Validators.compose([Validators.required, Validators.pattern(DEFAULT_PASSWORD_REGEXP)])],
        },
        {
          validator: FormsHelper.isEqual('password', 'newPassword'),
        },
      );
    }
  }

  ngOnDestroy() {
    this.organizationTypeSubscription.unsubscribe();
  }

  onSubmitPassword() {
    if (this.passwordForm.valid) {
      this.captchaElem.execute();
    }
  }

  proceedIfValid(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;
    this.proceedSubmission(captchaResponseV2);
  }

  private proceedSubmission(captchaResponseV2: string) {
    const password = this.passwordForm.get('password').value?.trim();
    const token = this.activatedRoute.snapshot.queryParamMap.get('token');
    const hostname = document.location.hostname;
    let observable: ObservableResult<void>;
    const captchaActionName = this.isEmailConfirmationPage ? 'folioEmailConfirmation' : 'passwordChange';

    this.captchaV3Service.execute(captchaActionName).subscribe((captchaResponseV3: string) => {
      if (this.isEmailConfirmationPage) {
        observable = this.accountService.confirmWorkspaceEmailWithPassword({
          token: token,
          password: password,
          captchaResponseV3: captchaResponseV3,
          captchaResponseV2: captchaResponseV2,
          type: this.tokenType,
        });
      } else {
        observable = this.accountService.updateUserPassword(password, token, captchaResponseV3, captchaResponseV2);
      }
      observable.subscribe(({ isSuccess, error }) => {
        if (isSuccess) {
          const extraParams = this.isEmailConfirmationPage
            ? {
                queryParams: {
                  email: this.passwordForm.controls['email'].value?.trim(),
                },
              }
            : undefined;
          if (this.isEmailConfirmationPage) {
            this.checkHostnameAndRedirectToLogin(hostname, extraParams);
          } else {
            RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin', extraParams);
          }
          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            this.isEmailConfirmationPage
              ? marker('translations.public.message.success.folioEmailConfirmed')
              : marker('translations.public.message.success.passwordUpdated'),
          );
        } else {
          if (this.isEmailConfirmationPage) {
            SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
          } else {
            RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/forgot-password', {
              queryParams: { expire: 1, message: error },
            });
          }
        }
        this.captchaElem.reset();
        this.cdr.detectChanges();
      });
    });
  }

  private checkHostnameAndRedirectToLogin(hostname: string, extraParams: { queryParams: { email: string } }) {
    if (!this.isFolioDomain(hostname) && this.organizationType === 'EDUCATIONAL') {
      if (this.tokenType === TokenType.ALUMNI_EMAIL_VERIFICATION) {
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/graduate/signin');
      } else {
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin', extraParams);
      }
    } else {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin', extraParams);
    }
  }

  private isFolioDomain(hostname: string): boolean {
    return hostname.startsWith('yuna.dev.potential.ly') || hostname.startsWith('yuna.potential.ly') || hostname.startsWith('localhost');
  }

  private getEmailFromToken(token: string) {
    const requestTokenType = this.getRequestTokenType();
    const requestParams: TokenVerification = { type: requestTokenType, token: token };
    this.accountService.getEmailByToken(requestParams).subscribe(({ value }) => {
      if (value) {
        this.passwordForm.controls['email'].setValue(value.email);
        this.tokenType = value.tokenType;
      }
    });
  }

  private getRequestTokenType() {
    let requestTokenType = null;
    if (this.router.url.includes('verify-personal-email')) {
      requestTokenType = TokenType.FOLIO_EMAIL_VERIFICATION;
    }
    if (this.router.url.includes('verify-alumni-email')) {
      requestTokenType = TokenType.ALUMNI_EMAIL_VERIFICATION;
    }
    if (this.router.url.includes('verify-organisation-email')) {
      requestTokenType = TokenType.PRE_ARRIVAL_ACCOUNT_INVITATION;
    }
    if (this.router.url.includes('verify-organisation-email-with-folio')) {
      requestTokenType = TokenType.PRE_ARRIVAL_ACCOUNT_INVITATION_WITH_FOLIO;
    }
    return requestTokenType;
  }
}
